import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HiOutlineCog6Tooth } from "react-icons/hi2";
import TradingPresetSettings from "./TradingPresetSettings";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TradeAccount,
  useTradeAccountsStore,
} from "@/lib/stores/userTradeAccounts";
import { useBinanceAccount } from "@/hooks/exchanges/useBinanceBalances";
import { useAccountInfoStore } from "@/lib/stores/accountInfoStore";

interface FloatingTradeWidgetProps {
  onBuy?: (volume: number, accountId?: string) => void;
  onSell?: (volume: number, accountId?: string) => void;
  style?: React.CSSProperties;
  onMouseDown?: (e: React.MouseEvent) => void;
  symbol: string;
}

const STORAGE_KEY = "trading_presets";

export default function FloatingTradeWidget({
  onBuy,
  onSell,
  style,
  onMouseDown,
  symbol,
}: FloatingTradeWidgetProps) {
  const [volume, setVolume] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [presets, setPresets] = useState<{ default: number[]; coin: number[] }>(
    {
      default: [1000, 2000, 3000, 4000, 5000],
      coin: [1000, 2000, 3000, 4000, 5000],
    },
  );
  const [isDefaultPreset, setIsDefaultPreset] = useState(true);
  const { accounts, selectedAccountId, setSelectedAccountId } =
    useTradeAccountsStore();

  // Filter accounts that have trading enabled
  const tradingAccounts = accounts.filter((account) => account.trading);

  // Find the selected account
  const selectedAccount =
    tradingAccounts.find((account) => account.id === selectedAccountId) || null;

  // Use the hook to initialize account info in the store
  useBinanceAccount(selectedAccount);

  // Get account info directly from the store
  const { getAccountInfo, isLoading } = useAccountInfoStore();
  const accountInfo = selectedAccountId
    ? getAccountInfo(selectedAccountId)
    : null;
  const loading = selectedAccountId ? isLoading(selectedAccountId) : false;

  // Set the first trading account as selected by default if none is selected
  useEffect(() => {
    if (tradingAccounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(tradingAccounts[0].id);
    }
  }, [tradingAccounts, selectedAccountId, setSelectedAccountId]);

  // Load presets from localStorage on mount
  useEffect(() => {
    try {
      const savedPresets = localStorage.getItem(STORAGE_KEY);
      if (savedPresets) {
        const parsed = JSON.parse(savedPresets);
        setPresets(parsed.presets);
        setIsDefaultPreset(parsed.isDefault);
      }
    } catch (error) {
      console.error("Error loading presets:", error);
    }
  }, []);

  // Handle symbol changes
  useEffect(() => {
    try {
      const savedPresets = localStorage.getItem(STORAGE_KEY);
      if (savedPresets) {
        const parsed = JSON.parse(savedPresets);
        // If we're in coin-specific mode, update the volume to match the corresponding preset in the new symbol
        if (!parsed.isDefault) {
          const oldVolume = volume;
          const oldPresets = parsed.presets.coin;
          const presetIndex = oldPresets.findIndex(
            (preset: number) => preset === oldVolume,
          );
          if (presetIndex !== -1) {
            setVolume(parsed.presets.coin[presetIndex]);
          }
        }
      }
    } catch (error) {
      console.error("Error handling symbol change:", error);
    }
  }, [symbol, volume]);

  const handleSavePresets = (
    newPresets: { default: number[]; coin: number[] },
    isDefault: boolean,
  ) => {
    // Find matching preset in the new preset type if the current volume matches any old preset
    const oldPresets = isDefaultPreset ? presets.default : presets.coin;
    const newPresetsArray = isDefault ? newPresets.default : newPresets.coin;
    const oldIndex = oldPresets.findIndex((preset) => preset === volume);

    setPresets(newPresets);
    setIsDefaultPreset(isDefault);

    // If we found a matching preset, update to the corresponding new preset
    if (oldIndex !== -1 && newPresetsArray[oldIndex]) {
      setVolume(newPresetsArray[oldIndex]);
    }

    // Save to localStorage
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          presets: newPresets,
          isDefault,
        }),
      );
    } catch (error) {
      console.error("Error saving presets:", error);
    }
  };

  const currentPresets = isDefaultPreset ? presets.default : presets.coin;

  return (
    <div className="flex flex-col" style={style}>
      <div className="bg-neutral-900 border rounded-lg rounded-b-none border-neutral-700 shadow-lg flex items-center h-8 z-50">
        {/* Drag Handle */}
        <div
          className="px-2 h-full flex items-center cursor-grab hover:bg-neutral-800 border-r border-neutral-700"
          onMouseDown={onMouseDown}
        >
          <svg
            width="8"
            height="16"
            viewBox="0 0 8 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="2" cy="2" r="1" fill="#666" />
            <circle cx="6" cy="2" r="1" fill="#666" />
            <circle cx="2" cy="8" r="1" fill="#666" />
            <circle cx="6" cy="8" r="1" fill="#666" />
            <circle cx="2" cy="14" r="1" fill="#666" />
            <circle cx="6" cy="14" r="1" fill="#666" />
          </svg>
        </div>

        {/* Buy Button */}
        <Button
          size="sm"
          className="h-6 w-6 mx-1 bg-green-600 hover:bg-green-700 text-white text-sm p-0"
          onClick={() => onBuy?.(volume, selectedAccountId || undefined)}
        >
          B
        </Button>

        {/* Volume Input */}
        <div className="relative flex items-center">
          <span className="absolute left-2 text-gray-400 text-sm">$</span>
          <Input
            type="number"
            min="0"
            step="any"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="h-6 w-24 pl-5 pr-2 mx-1 bg-neutral-800 text-white border-neutral-700 text-sm"
          />
        </div>

        {/* Sell Button */}
        <Button
          size="sm"
          className="h-6 w-6 mx-1 bg-red-600 hover:bg-red-700 text-white text-sm p-0"
          onClick={() => onSell?.(volume, selectedAccountId || undefined)}
        >
          S
        </Button>
      </div>

      {/* Position Size Buttons */}
      <div className="flex gap-[1px] mt-[1px] bg-neutral-900 border border-neutral-700">
        {/* Configure Button */}
        <Button
          size="sm"
          className="ml-1 h-4 w-4 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 hover:text-neutral-300 text-xs p-0"
          onClick={() => setShowSettings(true)}
        >
          <HiOutlineCog6Tooth size={10} />
        </Button>
        {currentPresets.map((preset, index) => {
          const isActive = volume === preset;
          return (
            <Button
              key={index}
              size="sm"
              className={`h-4 w-[19%] ${
                isDefaultPreset
                  ? `${isActive ? "bg-neutral-600 text-white" : "bg-neutral-800 text-neutral-300"}`
                  : `${isActive ? "bg-green-500" : "bg-green-600/80"} border ${isActive ? "border-green-400" : "border-green-500/50"} text-white`
              } hover:bg-neutral-700 text-xs p-0`}
              onClick={() => setVolume(preset)}
            >
              {index + 1}
            </Button>
          );
        })}
      </div>

      {/* Account Select - Placed at the bottom of the widget */}
      <div className="bg-neutral-900 border rounded-lg rounded-t-none border-neutral-700 shadow-lg flex items-center justify-between h-8 z-50 mt-[1px]">
        <div className="w-3/5 px-2">
          <Select
            value={selectedAccountId}
            onValueChange={setSelectedAccountId}
          >
            <SelectTrigger className="h-6 w-full bg-neutral-800 text-white border-neutral-700 text-xs outline-none focus:ring-0">
              <SelectValue placeholder="Account" />
            </SelectTrigger>
            <SelectContent className="bg-neutral-800 text-white border-neutral-700 z-[100]">
              {tradingAccounts.map((account) => (
                <SelectItem
                  key={account.id}
                  value={account.id}
                  className="text-xs"
                >
                  {account.label || account.exchange}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Balance Label */}
        <div className="px-2 text-xs text-gray-300">
          <span>
            ${parseFloat(accountInfo?.availableBalance || "0").toFixed(2)}
          </span>
        </div>
      </div>

      <TradingPresetSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        symbol={symbol}
        onSave={handleSavePresets}
        initialPresets={presets}
        initialIsDefault={isDefaultPreset}
      />
    </div>
  );
}
